/**
 * Eros — on-screen-keyboard inset (2026-08-19).
 *
 * WHY
 * On phones the virtual keyboard covers whatever sits at the bottom of the
 * viewport. Neither platform shrinks the LAYOUT viewport for it any more:
 *   - iOS Safari never did (fixed chrome stays put and gets covered; Safari
 *     also offsets the visual viewport inside the layout viewport).
 *   - Chrome on Android stopped in 108 — the default `interactive-widget`
 *     is `resizes-visual`, so only the VISUAL viewport shrinks.
 * Result: every `position: fixed` bottom-anchored surface in this theme (the
 * assistant composer in the drawer footer, the /cart pinned summary bar with
 * its order-note field, every native <dialog> `.eros-sheet`, the newsletter
 * `.pop-in`) keeps painting underneath the keyboard.
 *
 * WHAT
 * The only portable measurement is the VisualViewport API. Measure it once,
 * globally, and publish it four ways:
 *   :root { --eros-kb-inset: <px> }        consumed by assets/eros-keyboard-inset.css
 *   <html data-eros-keyboard="open|closed">  gate — every CSS rule is inert unless "open"
 *   window.ErosKeyboardInset = { inset, open }
 *   window event 'eros:keyboard-inset'      { detail: { inset, open } }
 *
 * env(keyboard-inset-height) is deliberately NOT the mechanism. It only
 * reports non-zero after opting into the VirtualKeyboard API
 * (navigator.virtualKeyboard.overlaysContent = true, Chromium only), which
 * would simultaneously stop Chrome resizing the visual viewport — i.e. it
 * would break this measurement on the one platform that supports it, and do
 * nothing on iOS/Firefox. One mechanism for all platforms instead.
 *
 * COST WHEN IDLE
 * Two passive listeners on window.visualViewport. They fire only on viewport
 * geometry changes (keyboard, pinch-zoom, URL-bar show/hide) — never on plain
 * document scrolling on desktop, and on mobile URL-bar frames they hit the
 * early return below (`inset === 0 && nothing editable focused`) without
 * reading layout at all. Every fire coalesces into at most one rAF; that rAF
 * reads three viewport properties and returns unless the value moved >= 1px.
 * No listeners are attached at all when visualViewport is absent.
 */
(function () {
  'use strict';

  var root = document.documentElement;
  var vv = window.visualViewport || null;

  /**
   * Noise floor. URL-bar animation frames, sub-pixel rounding and iOS
   * rubber-banding produce a few px of apparent inset with no keyboard.
   * iOS' hardware-keyboard accessory bar is ~44px and IS worth honouring,
   * so the floor stays below it.
   */
  var MIN_INSET_PX = 40;
  /** Only rewrite when the value actually moved. */
  var EPSILON_PX = 1;
  /** Pinch-zoom shrinks visualViewport.height too. That is not a keyboard. */
  var MAX_KEYBOARD_SCALE = 1.02;
  /** Gap kept between a focused field and the edge of the visible band. */
  var VISIBLE_MARGIN_PX = 8;
  /** OSK animation is ~250-300ms on both platforms. */
  var FOCUS_SETTLE_MS = 300;

  /**
   * Bottom-anchored chrome that eros-keyboard-inset.css already lifts. A
   * focused field inside one of these does NOT want scrollIntoView unless it
   * also has its own scrollport — scrolling would only move the page behind
   * the overlay (or nothing at all, since x-drawer/x-modal put `.lock`
   * (overflow:hidden) on <html> and <dialog> blocks scrolling itself).
   */
  var LIFTED_HOSTS = 'x-drawer, x-modal, x-popover, dialog, .pop-in, .cart-footer, [data-eros-kb-lifted]';

  /** input types that never raise a keyboard. */
  var NON_TYPING_INPUT = {
    hidden: 1,
    button: 1,
    submit: 1,
    reset: 1,
    checkbox: 1,
    radio: 1,
    file: 1,
    image: 1,
    range: 1,
    color: 1,
  };

  /** Published, mutated in place — consumers keep a live reference. */
  var state = { inset: 0, open: false };
  window.ErosKeyboardInset = state;

  var frame = 0;
  var focusTimer = 0;

  var coarseQuery = window.matchMedia ? window.matchMedia('(pointer: coarse)') : null;
  var reduceMotionQuery = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

  var raf =
    window.requestAnimationFrame ||
    function (cb) {
      return window.setTimeout(cb, 16);
    };

  function isCoarsePointer() {
    // Read at call time, not at load: 2-in-1s flip between pointer types.
    return coarseQuery ? coarseQuery.matches : 'ontouchstart' in window;
  }

  /* ------------------------------------------------------------------ focus */

  function isTypingTarget(el) {
    if (!el || el.nodeType !== 1) return false;
    var tag = el.tagName;
    if (tag === 'INPUT') return !NON_TYPING_INPUT[String(el.type || 'text').toLowerCase()];
    if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
    return el.isContentEditable === true;
  }

  /**
   * document.activeElement reports the shadow HOST when focus is inside a
   * shadow root (the theme's <x-drawer>/<x-modal> slot their fields, so this
   * mostly returns the field directly — but the descent keeps it correct for
   * any future closed-over field).
   */
  function activeEditable() {
    var el = document.activeElement;
    var guard = 0;
    while (el && el.shadowRoot && el.shadowRoot.activeElement && guard++ < 8) {
      el = el.shadowRoot.activeElement;
    }
    return isTypingTarget(el) ? el : null;
  }

  /* ---------------------------------------------------------------- measure */

  function measure() {
    if (!vv) return 0;
    if (vv.scale && vv.scale > MAX_KEYBOARD_SCALE) return 0;
    // The portable formula: how much of the layout viewport's bottom is not
    // covered by the visual viewport. offsetTop matters on iOS, where Safari
    // slides the visual viewport down inside an unchanged layout viewport.
    var inset = window.innerHeight - (vv.height + vv.offsetTop);
    // `!(x > y)` rather than `x <= y` so NaN lands on 0.
    if (!(inset > MIN_INSET_PX)) return 0;
    return Math.round(inset);
  }

  function publish() {
    frame = 0;

    // Zero work while the keyboard is closed: mobile browsers fire
    // visualViewport resize/scroll on every URL-bar transition, and there is
    // nothing to recompute for those. Bail before touching layout.
    if (state.inset === 0 && !activeEditable()) return;

    var next = measure();
    if (Math.abs(next - state.inset) < EPSILON_PX) return;

    var wasOpen = state.open;
    state.inset = next;
    state.open = next > 0;

    // Read above, write below — never interleaved.
    root.style.setProperty('--eros-kb-inset', next + 'px');
    if (state.open !== wasOpen) {
      root.setAttribute('data-eros-keyboard', state.open ? 'open' : 'closed');
    }

    try {
      window.dispatchEvent(
        new CustomEvent('eros:keyboard-inset', {
          detail: { inset: next, open: state.open },
        }),
      );
    } catch (e) {
      /* no CustomEvent constructor — the CSS var above is the contract that matters */
    }

    // Only on the closed -> open edge. Re-running on every frame of the OSK
    // animation would restart the smooth scroll and stutter.
    if (state.open && !wasOpen) scheduleFocusCheck(0);
  }

  function schedule() {
    if (frame) return;
    frame = raf(publish);
  }

  /* --------------------------------------------------- keep the field visible */

  function visibleBand() {
    // getBoundingClientRect() is in layout-viewport coordinates; the visible
    // band inside it is [offsetTop, offsetTop + height].
    if (vv) return { top: vv.offsetTop, bottom: vv.offsetTop + vv.height };
    return { top: 0, bottom: window.innerHeight };
  }

  /** Does the field sit inside a scrollport of its own (a sheet body, a list)? */
  function hasInnerScroller(el) {
    var node = el.parentElement;
    var guard = 0;
    while (node && guard++ < 40) {
      if (node.scrollHeight - node.clientHeight > 1) {
        var overflowY = window.getComputedStyle(node).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') return true;
      }
      if (node === document.body) break;
      node = node.parentElement;
    }
    return false;
  }

  function ensureFocusVisible() {
    focusTimer = 0;
    // Desktop focus is never hijacked.
    if (!isCoarsePointer()) return;

    var el = activeEditable();
    if (!el || typeof el.getBoundingClientRect !== 'function') return;

    var rect = el.getBoundingClientRect();
    if (!rect.height && !rect.width) return;

    var band = visibleBand();
    // Already comfortable — do nothing at all.
    if (rect.top >= band.top + VISIBLE_MARGIN_PX && rect.bottom <= band.bottom - VISIBLE_MARGIN_PX) {
      return;
    }

    // Bottom-anchored overlay chrome is lifted by CSS instead.
    if (el.closest && el.closest(LIFTED_HOSTS) && !hasInnerScroller(el)) return;

    var smooth = !(reduceMotionQuery && reduceMotionQuery.matches);
    try {
      el.scrollIntoView({
        block: 'center',
        inline: 'nearest',
        behavior: smooth ? 'smooth' : 'auto',
      });
    } catch (e) {
      try {
        el.scrollIntoView();
      } catch (e2) {
        /* detached mid-flight */
      }
    }
  }

  function scheduleFocusCheck(delayMs) {
    if (focusTimer) window.clearTimeout(focusTimer);
    focusTimer = window.setTimeout(function () {
      focusTimer = 0;
      // Two frames: one for the CSS var write to take effect, one for the
      // relayout it causes to settle, so the visibility test reads final geometry.
      raf(function () {
        raf(ensureFocusVisible);
      });
    }, delayMs);
  }

  /* ------------------------------------------------------------------ wiring */

  if (vv) {
    root.setAttribute('data-eros-keyboard', 'closed');
    vv.addEventListener('resize', schedule, { passive: true });
    vv.addEventListener('scroll', schedule, { passive: true });

    window.addEventListener(
      'pagehide',
      function () {
        if (frame && window.cancelAnimationFrame) window.cancelAnimationFrame(frame);
        frame = 0;
        if (focusTimer) window.clearTimeout(focusTimer);
        focusTimer = 0;
      },
      { passive: true },
    );

    // bfcache restore: geometry may differ from when the page was frozen.
    window.addEventListener('pageshow', schedule, { passive: true });
  }

  // Runs with or without visualViewport — on browsers too old for it the
  // visible band falls back to window.innerHeight, which those browsers DO
  // shrink for the keyboard (they resize the layout viewport).
  document.addEventListener(
    'focusin',
    function (event) {
      if (!isTypingTarget(event.target)) return;
      if (!isCoarsePointer()) return;
      scheduleFocusCheck(FOCUS_SETTLE_MS);
    },
    { passive: true },
  );

  document.addEventListener(
    'focusout',
    function () {
      if (focusTimer) window.clearTimeout(focusTimer);
      focusTimer = 0;
    },
    { passive: true },
  );
})();
